import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import {
  IdsMenuItemData,
  MENU_DEFAULTS, safeForAttribute
} from './ids-menu-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsElement from '../../core/ids-element';

import '../ids-icon/ids-icon';

import styles from './ids-menu-item.scss';
import type IdsToolbarMoreActions from '../ids-toolbar/ids-toolbar-more-actions';
import type IdsTabMore from '../ids-tabs/ids-tab-more';
import type IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';
import type IdsMenu from './ids-menu';
import type IdsMenuGroup from './ids-menu-group';
import type IdsIcon from '../ids-icon/ids-icon';
import IdsText from '../ids-text/ids-text';

const Base = IdsColorVariantMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Menu Item Component
 * @type {IdsMenuItem}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part menu-item - the menu item element
 * @part text - the text element
 * @part icon - the icon element
 * @part check - the selected check element
 */
@customElement('ids-menu-item')
@scss(styles)
export default class IdsMenuItem extends Base {
  shouldUpdate = true;

  /**
   * Build the menu item
   */
  constructor() {
    super();

    // Build state object
    this.state = {};
    Object.keys(MENU_DEFAULTS).forEach((prop) => {
      const attr = this.getAttribute(prop);
      this.state[prop] = attr || MENU_DEFAULTS[prop];
    });

    Object.defineProperty(this, 'tabIndex', {
      get: () => this.#tabIndex,
      set: (value) => { this.#tabIndex = value; },
      configurable: true,
      enumerable: true
    });
  }

  disconnectedCallback(): void {
    if (this.mo) {
      this.mo?.disconnect();
      this.mo = undefined;
    }
    this.removeEventListener('click', this.handleClick);
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template() {
    let disabledClass = '';
    let disabledAttr = '';
    if (this.state?.disabled) {
      disabledClass = ' disabled';
      disabledAttr = ' disabled';
    }

    // Check
    const check = this.templateCheck();

    // Icon
    const iconSlot = `<span class="ids-menu-item-icon" role="presentation"><slot name="icon"></slot></span>`;

    // Selected
    let selectedClass = '';
    if (this.state?.selected) selectedClass = ' selected';

    // Shortcut
    const shortcutSlot = `<span class="ids-menu-item-shortcuts" part="shortcuts"><slot name="shortcuts"></slot></span>`;
    let shortcutClass = '';
    if (this.shortcutKeys) shortcutClass = ' has-shortcuts';

    // Submenu
    let submenuClass = '';
    if (this.submenu) submenuClass = ' has-submenu';

    // Tabindex
    let tabindex = 'tabindex="0"';
    if (this.state?.tabIndex && !this.state?.disabled) tabindex = ` tabindex="${this.state.tabIndex}"`;

    // TextAlign
    let textClass = '';
    if (this.textAlign) textClass = ` text-${this.textAlign}`;

    // Pinnable
    const pinnableClass = this.pinnable ? ' has-icon pinnable' : '';
    const pinIcon = this.pinnable ? `<ids-icon icon="tack" class="ids-icon pin" size="small"></ids-icon>` : '';

    // Text
    const text = this.textContent ? this.textContent.trim() : '';
    const tooltipText = text.length > 0 && !(/[\n\r]/.test(text)) ? `tooltip="${text}"` : '';
    const textSlot = `<ids-text class="ids-menu-item-text" overflow="ellipsis"${tooltipText}><slot></slot></ids-text>`;

    let innerContent = `${check}${iconSlot}${textSlot}${shortcutSlot}${pinIcon}`;
    if (this.iconAlign === 'end') innerContent = `${check}${textSlot}${iconSlot}${shortcutSlot}`;

    // Main
    return `<div role="none" part="menu-item" class="ids-menu-item${disabledClass}${selectedClass}${shortcutClass}${submenuClass}${textClass}${pinnableClass}">
      <a role="menuitem" ${tabindex} ${disabledAttr}>
        ${innerContent}
      </a>
      <slot name="submenu"></slot>
    </div>`;
  }

  handleClick(e: MouseEvent) {
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  templateCheck() {
    return '<span class="check" part="check" role="presentation"></span>';
  }

  templateDisplayIcon(icon: string) {
    const viewbox = this.viewbox ? ` viewbox="${this.viewbox}"` : '';
    return `<ids-icon slot="icon" icon="${icon}"${viewbox} part="icon" class="ids-icon ids-menu-item-display-icon"></ids-icon>`;
  }

  templateShortcutKeys(shortcutText: string) {
    return `<span slot="shortcuts" class="shortcuts">${shortcutText}</span>`;
  }

  templateSubmenuIcon() {
    return `<ids-icon slot="icon" icon="dropdown" class="ids-icon ids-menu-item-submenu-icon"></ids-icon>`;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes as an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.HIDDEN,
      attributes.HIGHLIGHTED,
      attributes.ICON,
      attributes.ICON_ALIGN,
      attributes.INHERIT_COLOR,
      attributes.MAX_WIDTH,
      attributes.MIN_WIDTH,
      attributes.SELECTED,
      attributes.SHORTCUT_KEYS,
      attributes.SUBMENU,
      attributes.TARGET,
      attributes.TABINDEX,
      attributes.TEXT_ALIGN,
      attributes.TOGGLEABLE,
      attributes.VALUE,
      attributes.VIEWBOX
    ];
  }

  /**
   * Menu-level `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback() {
    if (!this.state) this.state = {};
    super.connectedCallback();
    this.refresh();
    this.attachEventHandlers();
    this.initTooltip();
    this.#updateRTL();
    this.shouldUpdate = true;
    this.mo = new MutationObserver(() => {
      this.initTooltip();
    });
    this.mo.observe(this, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Watches for changes
   * @property {MutationObserver} mo this button component's mutation observer
   */
  private mo?: MutationObserver;

  colorVariants = ['module-nav'];

  /**
   * @returns {Array<string>} Menu Item vetoable events
   */
  vetoableEventTypes = ['beforeselected', 'beforedeselected'];

  /**
   * Updates the visual state of this menu item
   * @private
   * @returns {void}
   */
  refresh() {
    this.tabIndex = this.state.tabIndex;
    this.detectHidden();
    this.detectSubmenu();
    this.detectSelectability();
    if (this.menu) this.decorateForIcon((this.menu as any).hasIcons);
  }

  /**
   * @returns {void}
   */
  attachEventHandlers() {
    // When any of this item's slots change, refresh the visual state of the item
    this.onEvent('slotchange', this.container, (e: Event) => {
      const slot = e.target as HTMLSlotElement;

      // If the default slot (text content) changes, restore icon if needed
      if (!slot.name && this.state.icon && !this.querySelector('ids-icon[slot="icon"]')) {
        this.appendIcon(this.state.icon);
      }

      this.refresh();
    });

    this.onLocaleChange = () => {
      this.#updateRTL();
    };

    // Enables keyboard navigation (Enter/Space) for JAWS and Windows Narrator screen readers.
    this.a?.addEventListener('click', () => {});

    if (this.pinnable) {
      const pinIcon = this.a?.querySelector<IdsIcon>('ids-icon.pin');
      pinIcon?.addEventListener('click', (e: any) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.pinned = !this.pinned;
        pinIcon.icon = this.pinned ? 'tack-filled' : 'tack';
        this.dispatchEvent(new CustomEvent('pinned', { bubbles: true, detail: { pinned: this.pinned } }));
      });
    }
  }

  /**
   * Updates RTL styling based on locale direction
   * @private
   */
  #updateRTL() {
    const isRTL = this.localeAPI.isRTL();

    if (isRTL) {
      this.container?.classList.add('rtl');
    } else {
      this.container?.classList.remove('rtl');
    }
  }

  /**
   * @readonly
   * @returns {HTMLAnchorElement} reference to the Menu Item's anchor
   */
  get a() {
    return this.shadowRoot?.querySelector('a');
  }

  /**
   * @readonly
   * @returns {any} ['IdsMenu'] reference to the parent IdsMenu component, if one exists.
   */
  get menu(): IdsMenu | IdsPopupMenu | null {
    const toolbarParent = this.closest<IdsToolbarMoreActions>('ids-toolbar-more-actions');
    if (toolbarParent) {
      return toolbarParent.menu;
    }

    const tabMoreParent = this.closest<IdsTabMore>('ids-tab-more');
    if (tabMoreParent) {
      return tabMoreParent.container?.querySelector<IdsPopupMenu>('ids-popup-menu') || null;
    }

    return this.closest('ids-menu, ids-popup-menu');
  }

  /**
   * @readonly
   * @returns {IdsMenuGroup} ['IdsMenuGroup'] reference to the parent IdsMenuGroup component, if one exists.
   */
  get group() {
    return this.closest<IdsMenuGroup>('ids-menu-group');
  }

  /**
   * Passes a disabled attribute from the custom element to the button
   * @param {boolean} val true if the button will be disabled
   */
  set disabled(val: boolean) {
    // Handled as boolean attribute
    const shouldDisable = stringToBool(val);
    this.state.disabled = shouldDisable;

    const a = this.a;

    if (!a) return;

    const shouldUpdate = this.shouldUpdate;
    const isCurrentlyDisabled = stringToBool(this.getAttribute(attributes.DISABLED));

    if (shouldDisable) {
      (a as any).disabled = true;
      a.setAttribute(attributes.DISABLED, '');
      this.tabIndex = -1;
      this.container?.classList.add(attributes.DISABLED);
      this.addEventListener('click', this.handleClick);
      if (!isCurrentlyDisabled) {
        this.shouldUpdate = false;
        this.setAttribute(attributes.DISABLED, '');
        this.shouldUpdate = shouldUpdate;
      }
      return;
    }

    (a as any).disabled = false;
    a?.removeAttribute(attributes.DISABLED);
    this.tabIndex = 0;
    this.container?.classList.remove(attributes.DISABLED);
    if (isCurrentlyDisabled) {
      this.shouldUpdate = false;
      this.removeAttribute(attributes.DISABLED);
      this.removeEventListener('click', this.handleClick);
      this.shouldUpdate = shouldUpdate;
    }
  }

  /**
   * Retrieve the disabled state of the inner button element
   * @returns {boolean} the inner button's disabled state
   */
  get disabled(): boolean {
    return this.state.disabled;
  }

  /**
   * Get data of menu item
   * @returns {IdsMenuItemData} the data based on the component
   */
  get data(): IdsMenuItemData {
    return {
      id: this.id,
      type: 'item',
      value: this.value,
      text: this.text || this.textContent || undefined,
      disabled: this.disabled,
      selected: this.selected,
      icon: this.icon || this.iconEl?.icon,
      submenu: this.submenu?.data
    };
  }

  /**
   * Handles global html hidden attribute changes
   * @param {boolean} value hidden value
   */
  onHiddenChange(value: boolean | string) {
    const newValue = stringToBool(value);
    if (newValue) {
      this.setAttribute(attributes.HIDDEN, '');
      this.container?.classList.add(attributes.HIDDEN);
    } else {
      this.removeAttribute(attributes.HIDDEN);
      this.container?.classList.remove(attributes.HIDDEN);
    }
  }

  /**
   * @param {boolean} val true if the menu item should appear highlighted
   */
  set highlighted(val) {
    const trueVal = stringToBool(val);

    // Don't highlight if the item is disabled.
    if (trueVal && this.disabled) {
      return;
    }

    if (trueVal) {
      this.setAttribute(attributes.HIGHLIGHTED, 'true');
    } else {
      this.removeAttribute(attributes.HIGHLIGHTED);
    }

    this.state.highlighted = trueVal;
    this.container?.classList[trueVal ? 'add' : 'remove']('highlighted');
  }

  /**
   * @returns {boolean} true if the menu item should show a highlight
   */
  get highlighted() {
    return this.state.highlighted;
  }

  /**
   * Causes a menu item to become focused (and therefore highlighted).
   * @returns {void}
   */
  highlight() {
    this.highlighted = true;
    this.triggerEvent('highlighted', this, { bubbles: true, detail: { elem: this } });
  }

  /**
   * Causes a menu item to become unhighlighted.
   * @returns {void}
   */
  unhighlight() {
    this.highlighted = false;
  }

  /**
   * @param {string | undefined} val representing the icon to set
   */
  set icon(val) {
    if (typeof val !== 'string' || !val.length) {
      this.removeAttribute(attributes.ICON);
      this.state.icon = undefined;
      this.removeIcon();
    } else {
      this.state.icon = val;
      this.setAttribute(attributes.ICON, val);
      this.appendIcon(val);
    }

    if (this.group && typeof this.group.updateIconAlignment === 'function') {
      this.group.updateIconAlignment();
    }
  }

  /**
   * @returns {string | undefined} a defined IdsIcon's `icon` attribute, if one is present
   */
  get icon() {
    return this.iconEl?.icon || this.getAttribute(attributes.ICON);
  }

  /**
   * @returns {any} [IdsIcon | undefined] reference to a defined IDS Icon element, if applicable
   */
  get iconEl() {
    const icon = [...this.children].find((e) => e.matches('ids-icon')) as IdsIcon | undefined;
    return icon;
  }

  /**
   * Check if an icon exists, and adds the icon if it's missing
   * @param {string} iconName The icon name to check
   * @private
   */
  appendIcon(iconName: string) {
    // First look specifically for an icon slot.
    const icon = this.querySelector<IdsIcon>(`ids-icon[slot="icon"]`); // @TODO check for submenu icons here
    if (icon) {
      icon.icon = iconName;
    } else {
      this.insertAdjacentHTML('afterbegin', this.templateDisplayIcon(iconName));
    }
  }

  /**
   * Check if an icon exists, and removes the icon if it's present
   * @private
   */
  removeIcon() {
    const icon = this.querySelector(`ids-icon[slot="icon"]`); // @TODO check for submenu icons here
    if (icon) {
      icon.remove();
    }
  }

  /**
   * Updates the alignment of text/icon content in the menu item to account for icons
   * that are present either on this menu item, or another one inside this menu item's group.
   * @param {boolean} doShow true if the menu item should be decorated
   * @private
   */
  decorateForIcon(doShow?: boolean) {
    this.container?.classList[this.icon && doShow ? 'add' : 'remove']('has-icon');
  }

  /**
   * @param {boolean} val true if this trigger button should inherit a parent component's text color for use internally
   */
  set inheritColor(val: boolean | string) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.INHERIT_COLOR, '');
      this.container?.classList.add('inherit-color');
    } else {
      this.removeAttribute(attributes.INHERIT_COLOR);
      this.container?.classList.remove('inherit-color');
    }
  }

  /**
   * @returns {boolean} true if this trigger button inherits a parent component's text color for use internally
   */
  get inheritColor(): boolean {
    return this.hasAttribute(attributes.INHERIT_COLOR);
  }

  /**
   * @param {boolean | string} val true if the menu item is pinnable
   */
  set pinnable(val: boolean | string) {
    this.toggleAttribute(attributes.PINNABLE, stringToBool(val));
  }

  get pinnable(): boolean {
    return this.hasAttribute(attributes.PINNABLE);
  }

  /**
   * @param {boolean | string} val true if the menu item is pinned
   */
  set pinned(val: boolean | string) {
    if (this.pinnable) this.toggleAttribute(attributes.PINNED, stringToBool(val));
  }

  get pinned(): boolean {
    return this.pinnable && this.hasAttribute(attributes.PINNED);
  }

  /**
   *
   */
  detectHidden() {
    this.hidden = this.hasAttribute('hidden');
  }

  /**
   * @readonly
   * @returns {any} [IdsMenu | IdsPopupMenu] submenu component, if one is present.
   */
  get submenu(): IdsMenu | IdsPopupMenu | null {
    return this.querySelector('ids-menu, ids-popup-menu');
  }

  /**
   * @readonly
   * @returns {boolean} true if a submenu is present
   */
  get hasSubmenu() {
    // @TODO remove to enable submenu functionality in standalone menu
    const prototypeHasMenu = (this.menu?.tagName !== 'IDS-MENU') || false;
    return prototypeHasMenu && !!this.submenu;
  }

  /**
   * @private
   * @returns {boolean} true if this menu item contains a submenu structure.
   */
  detectSubmenu() {
    const hasSubmenu = this.hasSubmenu;
    this.container?.classList[hasSubmenu ? 'add' : 'remove']('has-submenu');
    this.decorateSubmenu(hasSubmenu);
    return hasSubmenu;
  }

  /**
   * @private
   * @param {boolean|string} val true if a submenu is present and should be identified
   * with icons and correct aria attributes
   */
  decorateSubmenu(val: boolean | string) {
    const icon = this.container?.querySelector('ids-icon[icon="dropdown"]');
    if (val === true || val === 'true') {
      if (this.submenu) {
        this.submenu.setAttribute(htmlAttributes.SLOT, 'submenu');
        this.submenu.setAttribute(htmlAttributes.ARIA_EXPANDED, (this.submenu as IdsPopupMenu)?.visible ? 'true' : 'false');
      }
      this.a?.setAttribute(htmlAttributes.ROLE, 'button');
      this.a?.setAttribute(htmlAttributes.ARIA_HASPOPUP, 'true');
      if (!icon) {
        this.a?.insertAdjacentHTML('beforeend', this.templateSubmenuIcon());
      }
      this.value = null;
    } else {
      if (this.submenu) {
        this.submenu.removeAttribute(htmlAttributes.ARIA_EXPANDED);
      }
      this.a?.setAttribute(htmlAttributes.ROLE, 'menuitem');
      this.a?.removeAttribute(htmlAttributes.ARIA_HASPOPUP);
      icon?.remove();
    }
  }

  /**
   * @returns {boolean} true if this item is able to be selected
   */
  get isSelectable(): boolean {
    return this.group?.select !== null;
  }

  /**
   * Decorates the menu for selectability, adding/removing a checkmark
   * @private
   * @returns {void}
   */
  detectSelectability() {
    const selectType = this.group?.select;
    const check = this.container?.querySelector('span.check');

    if (this.isSelectable) {
      this.container?.classList.add(selectType === 'multiple' ? 'has-multi-checkmark' : 'has-checkmark');
      this.container?.classList.remove(selectType === 'multiple' ? 'has-checkmark' : 'has-multi-checkmark');
      if (!check) {
        this.a?.insertAdjacentHTML('afterbegin', this.templateCheck());
      }
      this.a?.setAttribute(htmlAttributes.ROLE, selectType === 'multiple' ? 'menuitemcheckbox' : 'menuitemradio');
      this.a?.setAttribute(htmlAttributes.ARIA_CHECKED, this.selected ? 'true' : 'false');
    } else {
      this.container?.classList.remove('has-checkmark', 'has-multi-checkmark');
      check?.remove();
      this.a?.setAttribute(htmlAttributes.ROLE, this.hasSubmenu ? 'button' : 'menuitem');
      this.a?.removeAttribute(htmlAttributes.ARIA_CHECKED);
    }
    if (this.iconAlign === 'end') this.container?.classList.add('align-icon-end');
  }

  /**
   * @returns {boolean} true if this item is currently selected
   */
  get selected() {
    return this.state.selected;
  }

  /**
   * @param {boolean} val true if the item should be selected
   */
  set selected(val) {
    // Determine true state and event names
    const trueVal = stringToBool(val);
    const duringEventName = trueVal ? 'selected' : 'deselected';
    const beforeEventName = `before${duringEventName}`;

    // Trigger a veto-able event for the specified selection type
    if (!this.triggerVetoableEvent(beforeEventName, { value: this.value })) {
      return;
    }

    if (this.state.selected !== trueVal) {
      if (this.isSelectable) {
        this.state.selected = trueVal;

        if (trueVal) {
          this.setAttribute(attributes.SELECTED, '');
        } else {
          this.removeAttribute(attributes.SELECTED);
        }

        this.container?.classList[trueVal ? 'add' : 'remove']('selected');
        this.a?.setAttribute(htmlAttributes.ARIA_CHECKED, trueVal ? 'true' : 'false');
      }

      // Build/Fire a `selected` event for performing other actions.
      // This event only fires when selection type changes.
      if (trueVal || this.group?.select === 'multiple' || this.toggleable) {
        this.triggerEvent(duringEventName, this, {
          bubbles: true,
          detail: {
            elem: this,
            value: this.value
          }
        });

        // This event always fires
        this.triggerEvent('pick', this, {
          bubbles: true,
          detail: {
            elem: this,
            value: this.value,
            selected: trueVal
          }
        });
      }
    }
  }

  /**
   * @returns {void}
   */
  select() {
    this.selected = true;
  }

  /**
   * @returns {void}
   */
  deselect() {
    this.selected = false;
  }

  /**
   * @param {string | undefined} val representing the shortcut key text
   */
  set shortcutKeys(val) {
    if (typeof val !== 'string' || !val.length) {
      this.removeAttribute(attributes.SHORTCUT_KEYS);
      this.state.shortcutKeys = undefined;
      this.removeShortcuts();
    } else {
      this.state.shortcutKeys = val;
      this.setAttribute(attributes.SHORTCUT_KEYS, val);
      this.appendShortcuts(val);
    }
  }

  /**
   * @returns {string | undefined} defined shortcut key text
   */
  get shortcutKeys() {
    return this.shortcutKeyEl?.textContent;
  }

  /**
   * @returns {HTMLSpanElement | undefined} reference to a span containing shortcut key text, if applicable
   */
  get shortcutKeyEl() {
    const span = [...this.children].find((e) => e.matches('span[slot="shortcuts"]')) as HTMLSpanElement | undefined;
    return span;
  }

  /**
   * Check if an icon exists, and adds the icon if it's missing
   * @param {string} shortcutText The icon name to check
   * @private
   */
  appendShortcuts(shortcutText: string) {
    // First look specifically for an icon slot.
    this.container?.classList.add('has-shortcuts');
    const span = this.querySelector<HTMLSpanElement>(`span[slot="shortcuts"]`); // @TODO check for submenu icons here
    if (span) {
      span.textContent = shortcutText;
    } else {
      this.insertAdjacentHTML('afterbegin', this.templateShortcutKeys(shortcutText));
    }
  }

  /**
   * Check if an icon exists, and removes the icon if it's present
   * @private
   */
  removeShortcuts() {
    this.container?.classList.remove('has-shortcuts');
    const span = this.querySelector<HTMLSpanElement>(`span[slot="shortcuts"]`); // @TODO check for submenu icons here
    if (span) {
      span.remove();
    }
  }

  /**
   * Passes a tabindex attribute from the custom element to the hyperlink
   * @param {any} val [number|string] the tabindex value
   * @returns {void}
   */
  set #tabIndex(val) {
    const trueVal = Number(val);
    if (!this.state) this.state = {};
    if (this.state.tabIndex !== trueVal) {
      this.removeAttribute(attributes.TABINDEX);

      // Mirror tabindex on the shadow DOM anchor
      if (Number.isNaN(trueVal) || trueVal < -1) {
        this.state.tabIndex = 0;
        this.a?.setAttribute(attributes.TABINDEX, '0');
      } else {
        this.state.tabIndex = trueVal;
        this.a?.setAttribute(attributes.TABINDEX, `${trueVal}`);
      }
    }
  }

  /**
   * @returns {any} [number] the current tabindex number for the hyperlink
   */
  get #tabIndex() {
    return this.state?.tabIndex || 0;
  }

  #target: any = undefined;

  /**
   * @param {HTMLElement|undefined} element an element reference to use for triggering/responding to elements
   */
  set target(element: any) {
    const currentTarget = this.target;
    if (element !== currentTarget) {
      if (element instanceof HTMLElement || element instanceof SVGElement) {
        if (!element.isEqualNode(currentTarget)) {
          this.#target = element;
        }
      } else if (element === null || element === undefined) {
        this.#target = undefined;
      }
    }
  }

  /**
   * @returns {HTMLElement|undefined} a reference to a target element, if applicable
   */
  get target() {
    return this.#target;
  }

  /**
   * @readonly
   * @returns {string} a menu item's textContent stripped of any extraneous white space.
   */
  get text() {
    const textNode = (n: any) => ((n.nodeType === Node.TEXT_NODE) || (n.name === 'ids-text'));
    return [...this.childNodes].find((i) => textNode(i))?.textContent?.trim();
  }

  set text(value: string | undefined) {
    if (!value) return;

    const childNodes = Array.from(this.childNodes);
    const textElement = this.shadowRoot?.querySelector<IdsText>('a ids-text');
    let textNode = childNodes.filter((node) => node.nodeType === Node.TEXT_NODE)[0];

    // Update text node
    if (!textNode) {
      textNode = document.createTextNode(value);
      this.appendChild(textNode);
    } else {
      textNode.textContent = value;
    }

    // Update tooltip text if ids-text is used
    if (textElement) {
      textElement.tooltip = value;
    }
  }

  /**
   * Set the value of the text align attribute
   * @param {string} val start / center / end
   * @memberof IdsMenuItem
   */
  set textAlign(val) {
    if (val) {
      this.setAttribute(attributes.TEXT_ALIGN, val);
    } else {
      this.removeAttribute(attributes.TEXT_ALIGN);
    }
  }

  /**
   * @readonly
   * @returns {string} a menu item's textAlign attribute
   */
  get textAlign() {
    return this.getAttribute(attributes.TEXT_ALIGN);
  }

  /**
   * Set the side the icon is on
   * @param {string} val start / center / end
   * @memberof IdsMenuItem
   */
  set iconAlign(val) {
    if (val) {
      this.setAttribute(attributes.ICON_ALIGN, val);
    } else {
      this.removeAttribute(attributes.ICON_ALIGN);
    }
  }

  /**
   * @readonly
   * @returns {string} a menu item's textAlign attribute
   */
  get iconAlign() {
    return this.getAttribute(attributes.ICON_ALIGN);
  }

  /**
   * @param {any} val the value for this menu item
   */
  set value(val) {
    this.state.value = val;

    // Don't display the value in the DOM.
    if (!safeForAttribute(val)) {
      const shouldUpdate = this.shouldUpdate;
      this.shouldUpdate = false;
      this.removeAttribute(attributes.VALUE);
      this.shouldUpdate = shouldUpdate;
    }
  }

  /**
   * @returns {any} the value of the menu item.
   */
  get value() {
    return this.state.value;
  }

  /**
   * Displays this menu item's submenu, if one is present.
   * @returns {void}
   */
  showSubmenu() {
    if (!this.hasSubmenu || (this.hasSubmenu && !this.submenu?.hidden)) {
      return;
    }
    this.a?.setAttribute(htmlAttributes.ARIA_EXPANDED, 'true');
    (this.menu as IdsPopupMenu)?.hideSubmenus(this);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (this.submenu as IdsPopupMenu)?.show();
  }

  /**
   * Hides this menu item's submenu, if one is present.
   * @returns {void}
   */
  hideSubmenu() {
    if (!this.hasSubmenu || (this.hasSubmenu && this.submenu?.hidden)) {
      return;
    }
    this.a?.setAttribute(htmlAttributes.ARIA_EXPANDED, 'false');
    (this.submenu as IdsPopupMenu)?.hide();
  }

  /**
   * Correctly focuses the menu item.  In this case, override the browser's default
   * focus routine and force focusing to occur on the anchor.
   * @returns {void}
   */
  focus() {
    if (!this.hidden && !this.disabled) {
      this.a?.focus();
      this.menu?.highlightItem(this);
    }
  }

  /**
   * Set icon viewbox
   * @param {string} value for icon viewbox
   */
  set viewbox(value) {
    if (value) {
      this.setAttribute(attributes.VIEWBOX, value);
    } else {
      this.removeAttribute(attributes.VIEWBOX);
    }
  }

  /**
   * Return the viewbox
   * @returns {string} the viewbox
   */
  get viewbox() {
    return this.getAttribute(attributes.VIEWBOX);
  }

  /**
   * For selectable menu items they may be toggleable for on/off
   * @param {boolean | string} value for icon viewbox
   */
  set toggleable(value) {
    const isToggleable = stringToBool(value);
    if (isToggleable) {
      this.setAttribute(attributes.TOGGLEABLE, 'true');
    } else {
      this.removeAttribute(attributes.TOGGLEABLE);
    }
  }

  /**
   * Return the viewbox
   * @returns {boolean} the viewbox
   */
  get toggleable() {
    return stringToBool(this.getAttribute(attributes.TOGGLEABLE));
  }

  /**
   * Set the minimum width for the menu item
   * @param {string} value CSS width value (e.g., '100px', '10rem')
   */
  set minWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MIN_WIDTH, value);
      this.container?.style.setProperty('--ids-menu-item-min-width', value);
    } else {
      this.removeAttribute(attributes.MIN_WIDTH);
      this.container?.style.removeProperty('--ids-menu-item-min-width');
    }
  }

  /**
   * Get the minimum width for the menu item
   * @returns {string | null} the minimum width value
   */
  get minWidth(): string | null {
    return this.getAttribute(attributes.MIN_WIDTH);
  }

  /**
   * Set the maximum width for the menu item
   * @param {string} value CSS width value (e.g., '200px', '15rem')
   */
  set maxWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.MAX_WIDTH, value);
      this.container?.style.setProperty('--ids-menu-item-max-width', value);
    } else {
      this.removeAttribute(attributes.MAX_WIDTH);
      this.container?.style.removeProperty('--ids-menu-item-max-width');
    }
  }

  /**
   * Get the maximum width for the menu item
   * @returns {string | null} the maximum width value
   */
  get maxWidth(): string | null {
    return this.getAttribute(attributes.MAX_WIDTH);
  }

  /**
   * Initializes tooltip for the menu item using its text content
   * @private
   */
  private initTooltip() {
    const text = this.textContent || '';
    const tooltipText = text.length > 0 && !(/[\n\r]/.test(text)) ? text : '';
    const textElement = this.shadowRoot?.querySelector('a ids-text');
    const hasToolTip = textElement?.hasAttribute(attributes.TOOLTIP);
    if (tooltipText && textElement && !hasToolTip) {
      textElement.setAttribute(attributes.TOOLTIP, tooltipText);
    }
  }
}
